import React, { useContext, useMemo } from "react"
import tw from "twin.macro"
import { LanguageContext } from "../../../contexts/Language"
import { firstLettersCapital } from "../../../helpers"
import { X } from "../../atoms/Icon"
import { Filter } from "../../../../src/clients/FiltersClient.d"
import {
  removeSelectedFilter,
  updateDealersFilter,
} from "../../../contexts/Filters/actions"

const FilterButtons = ({
  clearAllInventoryFilterQueue,
  dispatchFilters,
  clearFiltersModal,
  filters,
  filtersState,
}: {
  clearAllInventoryFilterQueue: () => void
  dispatchFilters: (action: any) => void
  clearFiltersModal: any
  filters: Filter[]
  filtersState: {
    selectedFilters: {
      [key: string]: any
    }
  }
}) => {
  const { _ } = useContext(LanguageContext)

  // Memoize active filters calculation to avoid recalculating on every render
  const allActiveFilters = useMemo(() => {
    if (!filters?.filter || filters.length === 0) {
      return []
    }
    // Filter the filters to only include active filters
    const activeFilters = filters?.filter(filter => {
      // Check if this filter has any selected values in the filters state
      const filterValues = filtersState.selectedFilters[filter.paramName]
      if (Array.isArray(filterValues)) {
        return filterValues.length > 0
      }
      return !!filterValues // Return true if the filter has a value
    })

    // Create static filter objects for the filters that might not be in the main filters array
    const staticActiveFilters = []

    // Add Price Filter if active
    if (
      filtersState.selectedFilters.priceMin ||
      filtersState.selectedFilters.priceMax
    ) {
      const priceFilter = {
        name: "Price",
        paramName: "price",
      }
      staticActiveFilters.push(priceFilter)
    }

    // Add Dealer Filter if active
    if (
      filtersState.selectedFilters.dealer &&
      filtersState.selectedFilters.dealer !== ""
    ) {
      const dealerFilter = {
        name: "Dealer",
        paramName: "dealer",
      }
      staticActiveFilters.push(dealerFilter)
    }

    // Combine all active filters
    return [...activeFilters, ...staticActiveFilters]
  }, [filters, filtersState.selectedFilters])

  return (
    <div
      css={[
        tw`min-h-[129px] flex flex-col gap-2 pb-5 border-b border-gray-200 w-full col-span-2 fixed bg-white top-[59px] px-4 z-[90] shadow-md`,
        tw`md:(px-8)`,
      ]}
    >
      <h5 css={[tw`text-lg mt-3 font-semibold inline`]}>
        {_("Filters")}
        {allActiveFilters.length === 0 && <> (0)</>}
      </h5>

      {allActiveFilters.length !== 0 && (
        <>
          <ul css={[tw`flex gap-4 overflow-scroll`]}>
            {allActiveFilters.map((filter, i: number) => {
              return (
                <li key={i}>
                  <button
                    css={[
                      tw`bg-wildSand rounded-full py-2 text-sm px-4 flex items-center gap-2 hocus:(bg-gray-200) whitespace-nowrap`,
                    ]}
                  >
                    <X
                      color="black"
                      css={[tw`w-4 p-1 bg-gray-400 rounded-full`]}
                      aria-label={`Remove ${filter.name} Filter`}
                      onClick={e => {
                        e.stopPropagation()

                        if (filter.paramName === "price") {
                          if (filtersState.selectedFilters.priceMin) {
                            dispatchFilters(
                              removeSelectedFilter(
                                "priceMin",
                                filtersState.selectedFilters.priceMin,
                              ),
                            )
                          }
                          if (filtersState.selectedFilters.priceMax) {
                            dispatchFilters(
                              removeSelectedFilter(
                                "priceMax",
                                filtersState.selectedFilters.priceMax,
                              ),
                            )
                          }
                        } else if (filter.paramName === "dealer") {
                          dispatchFilters(
                            removeSelectedFilter(
                              "dealer",
                              filtersState.selectedFilters.dealer,
                            ),
                          )
                          dispatchFilters(updateDealersFilter(null))
                        } else {
                          // If the filter is not price, remove the selected filter
                          dispatchFilters(
                            removeSelectedFilter(
                              filter.paramName,
                              filtersState.selectedFilters[filter.paramName],
                            ),
                          )
                        }
                      }}
                    />
                    {_(`${firstLettersCapital(filter.name)}`)}
                  </button>
                </li>
              )
            })}
          </ul>
          <button
            css={[
              tw`text-red-400 text-xs text-left font-semibold flex items-center gap-1 hocus:(text-red-500 underline)`,
            ]}
            onClick={() => {
              dispatchFilters(clearFiltersModal()),
                clearAllInventoryFilterQueue()
            }}
          >
            <X color="red-400" css={[tw`w-2`]} />
            {_("Clear All Filters")}
          </button>
        </>
      )}
    </div>
  )
}

export default FilterButtons
